// src/components/ParticipantsLayout.jsx
import React, { useState } from "react";
import LocalPreview from "./LocalPreview";
import RemoteTile from "./RemoteTile";

/**
 * ParticipantsLayout
 *
 * Regelt de layout van de cameratiles:
 *
 * - mode === "right": verticale kolom rechts van de video
 *   - showSelfView = true:
 *       LocalPreview boven
 *       daaronder alle remotes
 *       BIJ 0 REMOTES: Local boven + lege slots eronder (minimaal 1)
 *   - showSelfView = false:
 *       remoteCount = aantal remotes (excl. jij)
 *       - remoteCount === 0:
 *           1 placeholder-tile ("Wachten op deelnemer…")
 *       - remoteCount === 1:
 *           1 placeholder-tile (Bond-logo) + 1 remote + evt lege slots
 *       - remoteCount >= 2:
 *           remotes + evt lege slots
 *
 * - mode === "bottom": strip onder de video
 *   - showSelfView = true:
 *       LocalPreview links, daarnaast alle remotes + evt lege slots (minimaal 1 remote-slot)
 *   - showSelfView = false:
 *       remoteCount === 0:
 *         1 placeholder-tile + evt lege slots
 *       remoteCount === 1:
 *         2 tiles: placeholder (Bond) + remote + evt lege slots
 *       remoteCount >= 2:
 *         remotes + evt lege slots
 *
 * Props:
 *  - mode: "right" | "bottom"
 *  - layout: { rightColPct, bottomVh, tileAspect, ... } (uit App.js)
 *  - peerStreams: Map(peerId -> MediaStream)
 *  - soundEnabled: boolean
 *  - showTiles: boolean (bij video-only fullscreen -> false)
 *  - showSelfView: boolean (true = jezelf zien, false = eigen tile verbergen)
 *  - onLocalStreamChange(stream | null)
 *  - onToggleChat?: () => void   // chat open/dicht vanuit jouw eigen tile
 *  - expectedRemoteCount?: number // peerCount-1 uit session:state
 *  - onLocalMicChange?: (enabled: boolean) => void
 *  - unreadCount?: number
 *  - forceShowUiKey?: number
 */
export default function ParticipantsLayout({
  mode,
  layout,
  peerStreams,
  soundEnabled,
  showTiles = true,
  showSelfView = true,
  onLocalStreamChange,
  onToggleChat,
  expectedRemoteCount, // 👈 nieuw
  onLocalMicChange, // 👈 nieuw
  unreadCount = 0,
  forceShowUiKey = 0,
}) {
  const [audioPrefs, setAudioPrefs] = useState({});

  if (!showTiles) return null;

  const getVolume = (peerId) =>
    audioPrefs[peerId]?.volume !== undefined ? audioPrefs[peerId].volume : 1;

  const getMuted = (peerId) =>
    audioPrefs[peerId]?.muted !== undefined ? audioPrefs[peerId].muted : false;

  function handleVolumeChange(peerId, volume) {
    setAudioPrefs((prev) => ({
      ...prev,
      [peerId]: {
        volume,
        muted: volume === 0 ? true : false,
      },
    }));
  }

  function handleToggleMute(peerId) {
    setAudioPrefs((prev) => {
      const cur = prev[peerId] || { volume: 1, muted: false };
      return {
        ...prev,
        [peerId]: {
          volume: cur.volume ?? 1,
          muted: !cur.muted,
        },
      };
    });
  }

  const items = Array.from(peerStreams.entries()).map(([peerId, stream]) => ({
    peerId,
    stream,
  }));
  const remoteCount = items.length;

  // verwacht aantal remotes (minstens remoteCount)
  const expectedRemotes =
    typeof expectedRemoteCount === "number"
      ? Math.max(expectedRemoteCount, remoteCount)
      : remoteCount;

  const tileAspectRight = layout.tileAspect || "4 / 5";
  const tileAspectBottom = layout.tileAspect || "4 / 3";

  function BondPlaceholder({ message, align = "center" }) {
    return (
      <div
        style={{
          position: "relative",
          background: "#020617",
          borderRight: "1px solid #020617",
          borderBottom: "1px solid #020617",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: align === "left" ? "flex-start" : "center",
            justifyContent: "center",
            padding: 12,
            color: "#e5e7eb",
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              opacity: 0.9,
            }}
          >
            BOND
          </div>
          {message && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "#9ca3af",
                textAlign: align === "left" ? "left" : "center",
              }}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    );
  }

  function WaitingPlaceholder() {
    return (
      <div
        style={{
          position: "relative",
          background: "#000",
          borderRight: "1px solid #020617",
          borderBottom: "1px solid #020617",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#bbb",
            fontSize: 14,
            padding: 16,
            textAlign: "center",
          }}
        >
          Wachten op deelnemer…
        </div>
      </div>
    );
  }

  function EmptyPeerSlot() {
    return (
      <div
        style={{
          position: "relative",
          background: "#020617",
          borderRight: "1px solid #020617",
          borderBottom: "1px solid #020617",
        }}
      />
    );
  }

  // ===== RIGHT COLUMN =====
  if (mode === "right") {
    // SELF-VIEW AAN
    if (showSelfView) {
      // minimaal 1 remote-slot tonen zodat join “instant” voelt
      const expectedSlots = Math.max(1, expectedRemotes);
      const missingSlots = Math.max(0, expectedSlots - remoteCount);
      const rowCount = 1 + expectedSlots; // 1 local + expected remotes

      const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: `repeat(${rowCount}, 1fr)`,
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
        height: "100%",
        background: "#000",
      };

      return (
        <div style={colStyle}>
          <LocalPreview
            tileAspect={tileAspectRight}
            defaultMirror={true}
            onStreamChange={onLocalStreamChange}
            onToggleChat={onToggleChat}
            onMicChange={onLocalMicChange}
            unreadCount={unreadCount}
            forceShowUiKey={forceShowUiKey}
                        shouldStopOnUnmount={false}

          />

          {items.map(({ peerId, stream }) => (
            <RemoteTile
              key={peerId}
              peerId={peerId}
              stream={stream}
              soundEnabled={soundEnabled}
              tileAspect={tileAspectRight}
              volume={getVolume(peerId)}
              muted={getMuted(peerId)}
              onVolumeChange={handleVolumeChange}
              onToggleMute={handleToggleMute}
            />
          ))}

          {Array.from({ length: missingSlots }).map((_, i) => (
            <EmptyPeerSlot key={`missing-r-self-${i}`} />
          ))}
        </div>
      );
    }

    // SELF-VIEW UIT

    // remoteCount === 0 → alleen wacht-placeholder
    if (remoteCount === 0) {
      const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: "1fr",
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
        height: "100%",
        background: "#000",
      };

      return (
        <div style={colStyle}>
          <WaitingPlaceholder />
        </div>
      );
    }

    // remoteCount === 1 → placeholder + remote + evt lege slots
    if (remoteCount === 1) {
      const missingSlots = Math.max(0, expectedRemotes - remoteCount);
      const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: `repeat(${2 + missingSlots}, 1fr)`,
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
        height: "100%",
        background: "#000",
      };

      const onlyRemote = items[0];

      return (
        <div style={colStyle}>
          <BondPlaceholder message="Je eigen cam is verborgen." />
          <RemoteTile
            key={onlyRemote.peerId}
            peerId={onlyRemote.peerId}
            stream={onlyRemote.stream}
            soundEnabled={soundEnabled}
            tileAspect={tileAspectRight}
            volume={getVolume(onlyRemote.peerId)}
            muted={getMuted(onlyRemote.peerId)}
            onVolumeChange={handleVolumeChange}
            onToggleMute={handleToggleMute}
          />

          {Array.from({ length: missingSlots }).map((_, i) => (
            <EmptyPeerSlot key={`missing-r1-${i}`} />
          ))}
        </div>
      );
    }

    // remoteCount >= 2 → remotes + evt lege slots
    {
      const missingSlots = Math.max(0, expectedRemotes - remoteCount);
      const rowCount = expectedRemotes;

      const colStyle = {
        flex: `0 0 ${layout.rightColPct}%`,
        display: "grid",
        gridTemplateRows: `repeat(${rowCount}, 1fr)`,
        gridTemplateColumns: "1fr",
        gap: 0,
        padding: 0,
        minHeight: 0,
        height: "100%",
        background: "#000",
      };

      return (
        <div style={colStyle}>
          {items.map(({ peerId, stream }) => (
            <RemoteTile
              key={peerId}
              peerId={peerId}
              stream={stream}
              soundEnabled={soundEnabled}
              tileAspect={tileAspectRight}
              volume={getVolume(peerId)}
              muted={getMuted(peerId)}
              onVolumeChange={handleVolumeChange}
              onToggleMute={handleToggleMute}
            />
          ))}

          {Array.from({ length: missingSlots }).map((_, i) => (
            <EmptyPeerSlot key={`missing-r2-${i}`} />
          ))}
        </div>
      );
    }
  }

  // ===== BOTTOM STRIP =====
  if (mode === "bottom") {
    // SELF-VIEW AAN
    if (showSelfView) {
      const expectedSlots = Math.max(1, expectedRemotes); // minimaal 1 remote-slot
      const missingSlots = Math.max(0, expectedSlots - remoteCount);
      const totalTiles = 1 + expectedSlots;

      const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: `repeat(${totalTiles}, 1fr)`,
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };

      return (
        <div style={stripStyle}>
          <LocalPreview
            tileAspect={tileAspectBottom}
            defaultMirror={true}
            onStreamChange={onLocalStreamChange}
            onToggleChat={onToggleChat}
            onMicChange={onLocalMicChange}
            unreadCount={unreadCount}
            forceShowUiKey={forceShowUiKey}
                        shouldStopOnUnmount={false}

          />

          {items.map(({ peerId, stream }) => (
            <RemoteTile
              key={peerId}
              peerId={peerId}
              stream={stream}
              soundEnabled={soundEnabled}
              tileAspect={tileAspectBottom}
              volume={getVolume(peerId)}
              muted={getMuted(peerId)}
              onVolumeChange={handleVolumeChange}
              onToggleMute={handleToggleMute}
            />
          ))}

          {Array.from({ length: missingSlots }).map((_, i) => (
            <EmptyPeerSlot key={`missing-b-self-${i}`} />
          ))}
        </div>
      );
    }

    // SELF-VIEW UIT

    // remoteCount === 0 → placeholder + evt lege slots
    if (remoteCount === 0) {
      const total = Math.max(1, expectedRemotes);
      const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: `repeat(${total}, 1fr)`,
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };

      return (
        <div style={stripStyle}>
          <WaitingPlaceholder />
          {Array.from({ length: Math.max(0, total - 1) }).map((_, i) => (
            <EmptyPeerSlot key={`missing-b0-${i}`} />
          ))}
        </div>
      );
    }

    // remoteCount === 1 → [Bond-placeholder | remote] + evt extra slots
    if (remoteCount === 1) {
      const total = Math.max(2, expectedRemotes);
      const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: `repeat(${total}, 1fr)`,
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };

      return (
        <div style={stripStyle}>
          <BondPlaceholder />

          <RemoteTile
            peerId={items[0].peerId}
            stream={items[0].stream}
            soundEnabled={soundEnabled}
            tileAspect={tileAspectBottom}
            volume={getVolume(items[0].peerId)}
            muted={getMuted(items[0].peerId)}
            onVolumeChange={handleVolumeChange}
            onToggleMute={handleToggleMute}
          />

          {Array.from({ length: Math.max(0, total - 2) }).map((_, i) => (
            <EmptyPeerSlot key={`missing-b1-${i}`} />
          ))}
        </div>
      );
    }

    // remoteCount >= 2 → remotes + evt lege slots tot expectedRemotes
    {
      const missingSlots = Math.max(0, expectedRemotes - remoteCount);
      const total = Math.max(2, expectedRemotes);

      const stripStyle = {
        height: `${layout.bottomVh}vh`,
        display: "grid",
        gridTemplateColumns: `repeat(${total}, 1fr)`,
        gridTemplateRows: "1fr",
        gap: 0,
        padding: 0,
        overflow: "hidden",
        background: "#000",
      };

      return (
        <div style={stripStyle}>
          {items.map(({ peerId, stream }) => (
            <RemoteTile
              key={peerId}
              peerId={peerId}
              stream={stream}
              soundEnabled={soundEnabled}
              tileAspect={tileAspectBottom}
              volume={getVolume(peerId)}
              muted={getMuted(peerId)}
              onVolumeChange={handleVolumeChange}
              onToggleMute={handleToggleMute}
            />
          ))}

          {Array.from({ length: missingSlots }).map((_, i) => (
            <EmptyPeerSlot key={`missing-b2-${i}`} />
          ))}
        </div>
      );
    }
  }

  return null;
}
